import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, TextField, Typography, CircularProgress } from '@mui/material';
import { updateUserBookProgress } from 'services/progress/progress.service';

/**
 * Inline reading progress: status cycle + optional % input.
 * @param {object} props
 * @param {object|null} props.user - Auth user (required for default save)
 * @param {number|string} props.bookId
 * @param {boolean} props.chosenForBookclub
 * @param {object|null|undefined} props.initialProgress - Server progress for this book
 * @param {(bookId: number|string, data: object) => Promise<void>} [props.saveProgress] - Override API (e.g. BooksContext optimistic update)
 * @param {(bookId: number|string, merged: object) => void} [props.onProgressUpdated] - After successful save (invalidate queries, lift state)
 * @param {boolean} [props.showStatusLine=true]
 */
const BookProgressControls = ({
  user,
  bookId,
  chosenForBookclub,
  initialProgress,
  saveProgress: saveProgressProp,
  onProgressUpdated,
  showStatusLine = true,
}) => {
  const [progress, setProgress] = useState(initialProgress ?? null);
  const [loading, setLoading] = useState(false);
  // null = not editing (show saved value); string = draft including '' so the user can clear "0"
  const [percentInput, setPercentInput] = useState(null);

  useEffect(() => {
    setProgress(initialProgress ?? null);
  }, [bookId, initialProgress]);

  // Clear draft when the book changes or saved percent is refreshed from the server
  // (e.g. reopen dialog after Update %), so the field shows the persisted value.
  useEffect(() => {
    setPercentInput(null);
  }, [bookId, initialProgress?.percentComplete, initialProgress?.status]);

  const saveProgress = useCallback(
    async (id, data) => {
      if (saveProgressProp) {
        await saveProgressProp(id, data);
        return;
      }
      if (!user) return;
      await updateUserBookProgress(user.uid, id, data);
    },
    [saveProgressProp, user],
  );

  const mergeAndNotify = useCallback(
    (updateData) => {
      let merged;
      setProgress((prev) => {
        merged = { ...prev, ...updateData };
        return merged;
      });
      if (onProgressUpdated && merged) {
        onProgressUpdated(bookId, merged);
      }
    },
    [bookId, onProgressUpdated],
  );

  const getButtonText = () => {
    const st = progress?.status;
    if (!progress || st === 'not_started' || st === 'not-started') {
      return 'Mark as Started';
    }
    if (st === 'reading') {
      return 'Mark as Finished';
    }
    if (st === 'finished') {
      return 'Mark as Started';
    }
    return 'Mark as Started';
  };

  const handleProgressUpdate = async () => {
    if (!user && !saveProgressProp) return;

    setLoading(true);
    try {
      const currentProgress = progress;
      let updateData = {};

      if (!currentProgress || currentProgress.status === 'not_started' || currentProgress.status === 'not-started') {
        updateData = {
          status: 'reading',
          startedAt: new Date(),
          privacy: 'public',
        };
      } else if (currentProgress.status === 'reading') {
        updateData = {
          status: 'finished',
          finishedAt: new Date(),
          percentComplete: 100,
          privacy: 'public',
        };
      } else if (currentProgress.status === 'finished') {
        updateData = {
          status: 'reading',
          startedAt: new Date(),
          finishedAt: null,
          percentComplete: null,
          privacy: 'public',
        };
      }

      await saveProgress(bookId, updateData);
      mergeAndNotify(updateData);
      setPercentInput(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePercentChange = (value) => {
    // Keep digits only; allow empty so the field can be cleared (including erasing a leading 0).
    if (value === '') {
      setPercentInput('');
      return;
    }
    if (!/^\d{1,3}$/.test(value)) return;
    const asInt = Number(value);
    if (asInt > 100) return;
    setPercentInput(value);
  };

  const updatePercentProgress = async () => {
    if (!user && !saveProgressProp) return;

    const raw = percentInput !== null ? percentInput : progress?.percentComplete ?? '';
    const percentInt = parseInt(String(raw), 10);
    if (Number.isNaN(percentInt) || percentInt < 0 || percentInt > 100) return;

    setLoading(true);
    try {
      let updateData = {
        percentComplete: percentInt,
        privacy: 'public',
      };

      if (percentInt === 100 && progress?.status === 'reading') {
        updateData = {
          ...updateData,
          status: 'finished',
          finishedAt: new Date(),
        };
      }

      await saveProgress(bookId, updateData);
      mergeAndNotify(updateData);
      setPercentInput(null);
    } finally {
      setLoading(false);
    }
  };

  const status = progress?.status;
  const showPercentRow =
    chosenForBookclub && (status === 'reading' || status === 'finished');

  const savedPercent =
    progress?.percentComplete != null && progress?.percentComplete !== ''
      ? String(progress.percentComplete)
      : '';
  const percentFieldValue = percentInput !== null ? percentInput : savedPercent;
  const isPercentDirty = percentInput !== null && percentInput !== savedPercent;
  const canSubmitPercent =
    isPercentDirty && percentInput !== '' && !Number.isNaN(parseInt(percentInput, 10));

  if (!chosenForBookclub) {
    return (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Not selected for book club reading yet.
      </Typography>
    );
  }

  return (
    <Box>
      {showStatusLine && progress && (
        <Typography variant="caption" display="block" sx={{ mb: 1 }}>
          Status: {progress.status}
          {progress.percentComplete != null && progress.percentComplete !== ''
            ? ` (${progress.percentComplete}%)`
            : ''}
        </Typography>
      )}

      <Button
        variant="outlined"
        size="small"
        onClick={handleProgressUpdate}
        disabled={loading}
        sx={{ mb: 1 }}
      >
        {loading ? <CircularProgress size={16} /> : getButtonText()}
      </Button>

      {showPercentRow && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            type="text"
            inputMode="numeric"
            placeholder="%"
            value={percentFieldValue}
            onChange={(e) => handlePercentChange(e.target.value)}
            onFocus={(e) => {
              if (percentInput === null) {
                setPercentInput(savedPercent);
              }
              // Select all so replacing a lone "0" is one keystroke
              e.target.select();
            }}
            inputProps={{
              min: 0,
              max: 100,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              'aria-label': 'Reading progress percent',
            }}
            sx={{
              width: 72,
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', md: '0.875rem' },
              },
            }}
          />
          <Button
            size="small"
            variant={isPercentDirty ? 'contained' : 'text'}
            color="primary"
            onClick={updatePercentProgress}
            disabled={loading || !canSubmitPercent}
            sx={{ textTransform: 'none', minWidth: isPercentDirty ? 88 : undefined }}
          >
            Update %
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BookProgressControls;
